/* ==========================================================================
   HUSSIENI HOME — LUXE
   Hero slideshow, quick view, shop-the-room markers, predictive search.
   No dependencies; transform/opacity only; every feature degrades to plain
   links when the script does not run.
   ========================================================================== */
(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------------
     Hero slideshow
     --------------------------------------------------------------------- */
  function hydrate(slide) {
    slide.querySelectorAll('[data-srcset]').forEach(function (el) {
      el.setAttribute('srcset', el.getAttribute('data-srcset'));
      el.removeAttribute('data-srcset');
    });
  }

  function initHero(root) {
    var slides = root.querySelectorAll('[data-hh-slide]');
    var dots = root.querySelectorAll('[data-hh-dot]');
    var toggle = root.querySelector('[data-hh-toggle]');
    var interval = parseInt(root.dataset.interval, 10) || 5000;
    if (slides.length < 2) return;

    root.style.setProperty('--hh-int', interval + 'ms');
    var index = 0;
    var timer = null;
    var userPaused = reduced;
    var hoverPaused = false;
    var started = 0;
    var remaining = interval;

    function show(n) {
      n = (n + slides.length) % slides.length;
      hydrate(slides[n]);
      hydrate(slides[(n + 1) % slides.length]);
      slides.forEach(function (s, i) {
        var on = i === n;
        s.classList.toggle('is-active', on);
        s.setAttribute('aria-hidden', on ? 'false' : 'true');
        var link = s.querySelector('[data-hh-slide-link]');
        if (link) link.tabIndex = on ? 0 : -1;
      });
      dots.forEach(function (d, i) {
        d.classList.toggle('is-active', i === n);
        d.classList.toggle('is-done', i < n);
        if (i === n) d.setAttribute('aria-current', 'true'); else d.removeAttribute('aria-current');
        /* restart the progress animation */
        var bar = d.querySelector('span');
        if (bar && i === n) { bar.style.display = 'none'; void bar.offsetWidth; bar.style.display = ''; }
      });
      index = n;
      remaining = interval;
      schedule();
    }

    function schedule() {
      clearTimeout(timer);
      if (userPaused || hoverPaused || document.hidden) return;
      started = Date.now();
      timer = setTimeout(function () { show(index + 1); }, remaining);
    }

    function hold() {
      if (timer) {
        clearTimeout(timer);
        timer = null;
        remaining = Math.max(400, remaining - (Date.now() - started));
      }
      root.classList.add('is-paused');
    }

    function release() {
      if (userPaused || hoverPaused || document.hidden) return;
      root.classList.remove('is-paused');
      schedule();
    }

    function setUserPaused(p) {
      userPaused = p;
      if (toggle) toggle.setAttribute('aria-label', p ? toggle.dataset.labelPlay : toggle.dataset.labelPause);
      if (p) hold(); else { root.classList.remove('is-static'); release(); }
    }

    root.querySelector('[data-hh-next]').addEventListener('click', function () { show(index + 1); });
    root.querySelector('[data-hh-prev]').addEventListener('click', function () { show(index - 1); });
    dots.forEach(function (d) {
      d.addEventListener('click', function () { show(parseInt(d.dataset.hhDot, 10)); });
    });
    if (toggle) toggle.addEventListener('click', function () { setUserPaused(!userPaused); });

    /* Hovering the copy and controls pauses; resting the pointer on the
       photograph does not, or a full-screen hero would never advance. */
    var copy = root.querySelector('.hh-hs__copy');
    copy.addEventListener('mouseenter', function () { hoverPaused = true; hold(); });
    copy.addEventListener('mouseleave', function () { hoverPaused = false; release(); });
    root.addEventListener('focusin', function () { hoverPaused = true; hold(); });
    root.addEventListener('focusout', function (e) {
      if (!root.contains(e.relatedTarget)) { hoverPaused = false; release(); }
    });
    document.addEventListener('visibilitychange', function () { if (document.hidden) hold(); else release(); });

    /* Swipe on the image */
    var media = root.querySelector('.hh-hs__media');
    var x0 = null;
    media.addEventListener('touchstart', function (e) { x0 = e.touches[0].clientX; }, { passive: true });
    media.addEventListener('touchend', function (e) {
      if (x0 === null) return;
      var dx = e.changedTouches[0].clientX - x0;
      x0 = null;
      if (Math.abs(dx) < 40) return;
      var rtl = document.documentElement.dir === 'rtl';
      show(index + ((dx < 0) !== rtl ? 1 : -1));
    }, { passive: true });

    if (reduced) {
      root.classList.add('is-paused', 'is-static');
      if (toggle) toggle.setAttribute('aria-label', toggle.dataset.labelPlay);
    }

    /* Load the next slide only after the page itself has finished. */
    function afterLoad() { hydrate(slides[1]); schedule(); }
    if (document.readyState === 'complete') afterLoad();
    else window.addEventListener('load', afterLoad, { once: true });
  }

  /* ---------------------------------------------------------------------
     Quick view
     --------------------------------------------------------------------- */
  var qv = null;
  var qvOpener = null;

  function buildDialog() {
    qv = document.createElement('dialog');
    qv.className = 'hh-qv';
    qv.setAttribute('aria-labelledby', 'HHQvTitle');
    qv.innerHTML = '<button type="button" class="hh-qv__close" aria-label="Close">&times;</button><div class="hh-qv__frame"></div>';
    document.body.appendChild(qv);
    qv.querySelector('.hh-qv__close').addEventListener('click', function () { qv.close(); });
    qv.addEventListener('click', function (e) { if (e.target === qv) qv.close(); });
    qv.addEventListener('close', function () { if (qvOpener) qvOpener.focus(); });
    qv.addEventListener('click', onQvAdd);
  }

  function onQvAdd(e) {
    var btn = e.target.closest('[data-hh-qv-add]');
    if (!btn) return;
    var msg = qv.querySelector('[data-hh-qv-msg]');
    btn.disabled = true;
    fetch((window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add') + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: parseInt(btn.dataset.hhQvAdd, 10), quantity: 1 }] })
    })
      .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, body: j }; }); })
      .then(function (res) {
        btn.disabled = false;
        if (!res.ok) { msg.textContent = res.body.description || res.body.message || 'Could not add to cart.'; return; }
        msg.innerHTML = 'Added to your cart. <a href="' + ((window.routes && window.routes.cart_url) || '/cart') + '">View cart &rarr;</a>';
        return fetch('/cart.js').then(function (r) { return r.json(); }).then(function (cart) {
          var cartLink = document.querySelector('.hh-hdr__cart');
          if (!cartLink) return;
          var count = cartLink.querySelector('.hh-hdr__count');
          if (!count) { count = document.createElement('span'); count.className = 'hh-hdr__count'; count.setAttribute('aria-hidden', 'true'); cartLink.appendChild(count); }
          count.textContent = cart.item_count;
        });
      })
      .catch(function () { btn.disabled = false; msg.textContent = 'Could not add to cart. Please try again.'; });
  }

  function openQuickView(url, opener) {
    if (!qv) buildDialog();
    qvOpener = opener;
    var frame = qv.querySelector('.hh-qv__frame');
    frame.innerHTML = '<p class="hh-qv__loading">Loading…</p>';
    if (typeof qv.showModal === 'function') qv.showModal(); else { window.location.href = url; return; }
    var u = url + (url.indexOf('?') > -1 ? '&' : '?') + 'section_id=hh-quick-view';
    fetch(u, { headers: { Accept: 'text/html' } })
      .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var body = doc.querySelector('[data-hh-qv-body]');
        if (!body) throw new Error('empty');
        frame.innerHTML = '';
        frame.appendChild(body);
        var t = frame.querySelector('.hh-qv__title');
        if (t) { t.setAttribute('tabindex', '-1'); t.focus(); }
      })
      .catch(function () { window.location.href = url; });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-hh-qv]');
    if (!btn) return;
    e.preventDefault();
    openQuickView(btn.dataset.hhQv, btn);
  });

  /* ---------------------------------------------------------------------
     Shop the room
     --------------------------------------------------------------------- */
  function initRoom(root) {
    var spots = root.querySelectorAll('[data-hh-spot]');
    var items = root.querySelectorAll('[data-hh-spot-item]');

    function hot(n, on) {
      spots.forEach(function (s) { if (s.dataset.hhSpot === n) s.classList.toggle('is-hot', on); });
      items.forEach(function (i) { if (i.dataset.hhSpotItem === n) i.classList.toggle('is-hot', on); });
    }
    function closeAll(except) {
      spots.forEach(function (s) {
        if (s === except) return;
        var b = s.querySelector('button');
        var p = s.querySelector('.hh-room2__pop');
        b.setAttribute('aria-expanded', 'false');
        p.hidden = true;
        s.classList.remove('is-open');
      });
    }
    spots.forEach(function (s) {
      var b = s.querySelector('button');
      var p = s.querySelector('.hh-room2__pop');
      b.addEventListener('click', function (e) {
        e.stopPropagation();
        var open = b.getAttribute('aria-expanded') !== 'true';
        closeAll(s);
        b.setAttribute('aria-expanded', open ? 'true' : 'false');
        p.hidden = !open;
        s.classList.toggle('is-open', open);
      });
      s.addEventListener('mouseenter', function () { hot(s.dataset.hhSpot, true); });
      s.addEventListener('mouseleave', function () { hot(s.dataset.hhSpot, false); });
    });
    items.forEach(function (i) {
      i.addEventListener('mouseenter', function () { hot(i.dataset.hhSpotItem, true); });
      i.addEventListener('mouseleave', function () { hot(i.dataset.hhSpotItem, false); });
      i.addEventListener('focusin', function () { hot(i.dataset.hhSpotItem, true); });
      i.addEventListener('focusout', function () { hot(i.dataset.hhSpotItem, false); });
    });
    document.addEventListener('click', function (e) { if (!root.contains(e.target)) closeAll(null); });
    root.addEventListener('keydown', function (e) { if (e.key === 'Escape') closeAll(null); });
  }

  /* ---------------------------------------------------------------------
     Predictive search (Shopify /search/suggest.json)
     --------------------------------------------------------------------- */
  function esc(s) { return String(s || '').replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }

  function initSearch() {
    var panel = document.querySelector('[data-hh-search]');
    if (!panel) return;
    var input = panel.querySelector('[data-hh-search-input]');
    var out = panel.querySelector('[data-hh-search-results]');
    var suggest = panel.querySelector('[data-hh-search-suggest]');
    var last = null;
    var debounce = null;
    var ctrl = null;

    function open(e) {
      if (e) e.preventDefault();
      last = document.activeElement;
      panel.hidden = false;
      document.body.classList.add('hh-search-open');
      requestAnimationFrame(function () { panel.classList.add('is-open'); input.focus(); });
    }
    function close() {
      panel.classList.remove('is-open');
      document.body.classList.remove('hh-search-open');
      setTimeout(function () { panel.hidden = true; }, reduced ? 0 : 320);
      if (last) last.focus();
    }
    document.querySelectorAll('[data-hh-search-open]').forEach(function (a) { a.addEventListener('click', open); });
    panel.querySelector('[data-hh-search-close]').addEventListener('click', close);
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && !panel.hidden) close(); });

    function render(res, q) {
      var r = res.resources.results;
      var html = '';
      if (r.queries && r.queries.length) {
        html += '<div class="hh-sr__group"><p class="hh-label">Suggestions</p><ul class="hh-sr__terms" role="list">' +
          r.queries.slice(0, 5).map(function (s) { return '<li><a href="' + esc(s.url) + '">' + (s.styled_text || esc(s.text)) + '</a></li>'; }).join('') + '</ul></div>';
      }
      if (r.collections && r.collections.length) {
        html += '<div class="hh-sr__group"><p class="hh-label">Collections</p><ul class="hh-sr__cols" role="list">' +
          r.collections.slice(0, 6).map(function (c) { return '<li><a href="' + esc(c.url) + '">' + esc(c.title) + '</a></li>'; }).join('') + '</ul></div>';
      }
      if (r.products && r.products.length) {
        html += '<div class="hh-sr__group"><p class="hh-label">Pieces</p><ul class="hh-sr__prods" role="list">' +
          r.products.slice(0, 8).map(function (p) {
            var img = p.featured_image && p.featured_image.url ? p.featured_image.url : p.image;
            var src = img ? img + (img.indexOf('?') > -1 ? '&' : '?') + 'width=360' : '';
            return '<li><a href="' + esc(p.url) + '">' + (src ? '<img src="' + esc(src) + '" alt="" loading="lazy" width="360" height="450">' : '') +
              '<span class="hh-sr__name">' + esc(p.title) + '</span><span class="hh-sr__price">' + esc(money(Math.round(parseFloat(p.price) * 100))) + '</span></a></li>';
          }).join('') + '</ul></div>';
      }
      html += '<a class="hh-arrowlink hh-sr__all" href="/search?q=' + encodeURIComponent(q) + '&options%5Bprefix%5D=last">View all results for “' + esc(q) + '” <span aria-hidden="true">&rarr;</span></a>';
      out.innerHTML = html;
    }

    input.addEventListener('input', function () {
      var q = input.value.trim();
      clearTimeout(debounce);
      if (suggest) suggest.hidden = q.length > 0;
      if (q.length < 2) { out.innerHTML = ''; return; }
      debounce = setTimeout(function () {
        if (ctrl) ctrl.abort();
        ctrl = 'AbortController' in window ? new AbortController() : null;
        var url = '/search/suggest.json?q=' + encodeURIComponent(q) +
          '&resources[type]=product,collection,query&resources[limit]=8&resources[options][unavailable_products]=last';
        fetch(url, ctrl ? { signal: ctrl.signal } : {})
          .then(function (r) { return r.json(); })
          .then(function (j) { render(j, q); })
          .catch(function () {});
      }, 220);
    });
  }

  /* ---------------------------------------------------------------------
     Collection: column density (desktop) and load more
     --------------------------------------------------------------------- */
  function initCollection() {
    var grid = document.querySelector('[data-hh-grid]');
    if (!grid) return;
    var colBtns = document.querySelectorAll('[data-hh-cols]');
    function setCols(n) {
      grid.setAttribute('data-cols', n);
      colBtns.forEach(function (b) { b.setAttribute('aria-pressed', b.dataset.hhCols === String(n) ? 'true' : 'false'); });
      try { localStorage.setItem('hh-cols', n); } catch (e) {}
    }
    var saved = null;
    try { saved = localStorage.getItem('hh-cols'); } catch (e) {}
    if (saved) setCols(saved);
    colBtns.forEach(function (b) { b.addEventListener('click', function () { setCols(b.dataset.hhCols); }); });

    document.addEventListener('click', function (e) {
      var link = e.target.closest('[data-hh-more-link]');
      if (!link) return;
      e.preventDefault();
      if (link.getAttribute('aria-busy') === 'true') return;
      link.setAttribute('aria-busy', 'true');
      var url = link.href + (link.href.indexOf('?') > -1 ? '&' : '?') + 'section_id=' + link.dataset.section;
      fetch(url)
        .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var items = doc.querySelectorAll('[data-hh-grid] > li');
          var firstNew = null;
          items.forEach(function (li) { if (!firstNew) firstNew = li; grid.appendChild(li); });
          var more = document.querySelector('[data-hh-more]');
          var nextMore = doc.querySelector('[data-hh-more]');
          if (more) { if (nextMore) more.replaceWith(nextMore); else more.remove(); }
          var pager = document.querySelector('[data-hh-pager]');
          var nextPager = doc.querySelector('[data-hh-pager]');
          if (pager && nextPager) pager.replaceWith(nextPager);
          try { history.replaceState(null, '', link.href); } catch (err) {}
          if (firstNew) { var a = firstNew.querySelector('a'); if (a) a.focus({ preventScroll: true }); }
        })
        .catch(function () { window.location.href = link.href; });
    });
  }

  /* ---------------------------------------------------------------------
     Product gallery lightbox: full screen, swipe, keyboard, tap to zoom
     --------------------------------------------------------------------- */
  function initLightbox() {
    var shots = Array.prototype.slice.call(document.querySelectorAll('[data-hh-zoom]'));
    if (!shots.length) return;
    var dlg = null, track = null, counter = null, lastOpener = null;

    function build() {
      dlg = document.createElement('dialog');
      dlg.className = 'hh-lb';
      dlg.setAttribute('aria-label', 'Image viewer');
      dlg.innerHTML =
        '<div class="hh-lb__track" tabindex="0"></div>' +
        '<button type="button" class="hh-lb__close" aria-label="Close">&times;</button>' +
        '<button type="button" class="hh-lb__nav hh-lb__nav--prev" aria-label="Previous image"><svg viewBox="0 0 20 20" aria-hidden="true"><path d="M12.5 4.5 7 10l5.5 5.5"/></svg></button>' +
        '<button type="button" class="hh-lb__nav hh-lb__nav--next" aria-label="Next image"><svg viewBox="0 0 20 20" aria-hidden="true"><path d="M7.5 4.5 13 10l-5.5 5.5"/></svg></button>' +
        '<p class="hh-lb__count" aria-live="polite"></p><p class="hh-lb__hint">Tap the image to zoom</p>';
      document.body.appendChild(dlg);
      track = dlg.querySelector('.hh-lb__track');
      counter = dlg.querySelector('.hh-lb__count');
      shots.forEach(function (s, i) {
        var fig = document.createElement('figure');
        fig.className = 'hh-lb__slide';
        var img = document.createElement('img');
        img.alt = (s.querySelector('img') || {}).alt || '';
        img.dataset.src = s.dataset.hhZoom;
        img.decoding = 'async';
        fig.appendChild(img);
        track.appendChild(fig);
        fig.addEventListener('click', function (e) {
          var zoomed = fig.classList.toggle('is-zoomed');
          if (zoomed) {
            var r = fig.getBoundingClientRect();
            var fx = (e.clientX - r.left) / r.width, fy = (e.clientY - r.top) / r.height;
            requestAnimationFrame(function () {
              fig.scrollLeft = fx * (fig.scrollWidth - fig.clientWidth);
              fig.scrollTop = fy * (fig.scrollHeight - fig.clientHeight);
            });
          }
        });
      });
      dlg.querySelector('.hh-lb__close').addEventListener('click', function () { dlg.close(); });
      dlg.querySelector('.hh-lb__nav--prev').addEventListener('click', function () { go(current() - 1); });
      dlg.querySelector('.hh-lb__nav--next').addEventListener('click', function () { go(current() + 1); });
      dlg.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight') { e.preventDefault(); go(current() + 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); go(current() - 1); }
      });
      track.addEventListener('scroll', function () { update(); }, { passive: true });
      dlg.addEventListener('close', function () {
        document.documentElement.classList.remove('hh-lb-open');
        if (lastOpener) lastOpener.focus();
      });
    }
    function current() { return Math.round(track.scrollLeft / Math.max(1, track.clientWidth)); }
    function load(i) {
      [i - 1, i, i + 1].forEach(function (k) {
        var fig = track.children[k];
        if (!fig) return;
        var img = fig.querySelector('img');
        if (img.dataset.src) { img.src = img.dataset.src; delete img.dataset.src; }
      });
    }
    function update() {
      var i = current();
      load(i);
      counter.textContent = (i + 1) + ' / ' + shots.length;
      Array.prototype.forEach.call(track.children, function (f, k) { if (k !== i) f.classList.remove('is-zoomed'); });
    }
    function go(i) {
      i = Math.max(0, Math.min(shots.length - 1, i));
      track.scrollTo({ left: i * track.clientWidth, behavior: reduced ? 'auto' : 'smooth' });
      load(i);
    }
    shots.forEach(function (s, i) {
      s.setAttribute('tabindex', '0');
      s.setAttribute('role', 'button');
      s.setAttribute('aria-label', 'Open image ' + (i + 1) + ' full screen');
      function open(e) {
        if (e.type === 'keydown' && e.key !== 'Enter' && e.key !== ' ') return;
        if (e.type === 'keydown') e.preventDefault();
        if (!dlg) build();
        lastOpener = s;
        dlg.showModal();
        document.documentElement.classList.add('hh-lb-open');
        load(i);
        track.scrollLeft = i * track.clientWidth;
        update();
        track.focus();
      }
      s.addEventListener('click', open);
      s.addEventListener('keydown', open);
    });
  }

  /* ---------------------------------------------------------------------
     Recently viewed (this browser only)
     --------------------------------------------------------------------- */
  function money(cents) {
    var fmt = (window.theme && window.theme.settings && window.theme.settings.money_format) || '{{amount}}';
    var v = (cents / 100);
    var withDec = v.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var noDec = Math.round(v).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, function (m, k) {
      if (k === 'amount_no_decimals') return noDec;
      if (k === 'amount_with_comma_separator') return v.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      if (k === 'amount_no_decimals_with_comma_separator') return Math.round(v).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      return withDec;
    });
  }
  function initRecent() {
    var KEY = 'hh-recent';
    var list = [];
    try { list = JSON.parse(localStorage.getItem(KEY) || '[]'); } catch (e) { list = []; }
    var m = location.pathname.match(/\/products\/([^/?#]+)/);
    if (m) {
      var h = decodeURIComponent(m[1]);
      list = [h].concat(list.filter(function (x) { return x !== h; })).slice(0, 12);
      try { localStorage.setItem(KEY, JSON.stringify(list)); } catch (e) {}
    }
    var root = document.querySelector('[data-hh-recent]');
    if (!root) return;
    var limit = parseInt(root.dataset.limit, 10) || 4;
    var show = list.filter(function (x) { return x !== root.dataset.current; }).slice(0, limit);
    if (!show.length) return;
    Promise.all(show.map(function (h) {
      return fetch('/products/' + encodeURIComponent(h) + '.js').then(function (r) { return r.ok ? r.json() : null; }).catch(function () { return null; });
    })).then(function (items) {
      items = items.filter(Boolean);
      if (!items.length) return;
      root.querySelector('[data-hh-recent-list]').innerHTML = items.map(function (p) {
        var img = p.featured_image ? p.featured_image + (p.featured_image.indexOf('?') > -1 ? '&' : '?') + 'width=600' : '';
        return '<li><a class="hh-recent__card" href="' + esc(p.url) + '">' +
          (img ? '<span class="hh-recent__img"><img src="' + esc(img) + '" alt="" loading="lazy" width="600" height="750"></span>' : '') +
          '<span class="hh-recent__name">' + esc(p.title) + '</span>' +
          '<span class="hh-recent__price">' + esc(money(p.price)) + '</span></a></li>';
      }).join('');
      root.hidden = false;
    });
  }

  /* ---------------------------------------------------------------------
     Gallery-wall hero: panels change scene in a cascade
     --------------------------------------------------------------------- */
  function initGalleryHero(root) {
    var panels = root.querySelectorAll('.hh-hg__panel');
    var caps = root.querySelectorAll('[data-hh-cap-i]');
    var n = caps.length;
    if (n < 2) return;
    var interval = parseInt(root.dataset.interval, 10) || 5000;
    var bar = root.querySelector('[data-hh-hg-bar]');
    var now = root.querySelector('[data-hh-hg-now]');
    var toggle = root.querySelector('[data-hh-hg-toggle]');
    var index = 0, timer = null, started = 0, remaining = interval;
    var userPaused = reduced, hoverPaused = false;

    function hydrateScene(i) {
      root.querySelectorAll('[data-hh-shot-i="' + i + '"] img[data-srcset]').forEach(function (img) {
        var frame = img.closest('.hh-hg__panel');
        if (frame && getComputedStyle(frame).display === 'none') return;
        img.srcset = img.dataset.srcset; img.removeAttribute('data-srcset');
      });
    }
    function bar0() {
      if (!bar) return;
      bar.style.transition = 'none'; bar.style.transform = 'scaleX(0)'; void bar.offsetWidth;
      if (userPaused || hoverPaused) return;
      bar.style.transition = 'transform ' + remaining + 'ms linear'; bar.style.transform = 'scaleX(1)';
    }
    function show(i) {
      i = (i + n) % n;
      hydrateScene(i); hydrateScene((i + 1) % n); hydrateScene((i + 2) % n);
      panels.forEach(function (p) {
        var shots = p.querySelectorAll('.hh-hg__shot');
        shots.forEach(function (sh, k) {
          sh.classList.toggle('is-prev', k === index && k !== i);
          sh.classList.toggle('is-active', k === i);
        });
      });
      caps.forEach(function (c, k) {
        c.classList.toggle('is-active', k === i);
        var a = c.querySelector('[data-hh-cap-link]'); if (a) a.tabIndex = k === i ? 0 : -1;
      });
      if (now) now.textContent = ('0' + (i + 1)).slice(-2);
      index = i; remaining = interval; bar0(); schedule();
    }
    function schedule() {
      clearTimeout(timer);
      if (userPaused || hoverPaused || document.hidden) return;
      started = Date.now();
      timer = setTimeout(function () { show(index + 1); }, remaining);
    }
    function hold() {
      if (timer) { clearTimeout(timer); timer = null; remaining = Math.max(400, remaining - (Date.now() - started)); }
      root.classList.add('is-paused');
      if (bar) { var w = getComputedStyle(bar).transform; bar.style.transition = 'none'; bar.style.transform = w; }
    }
    function release() {
      if (userPaused || hoverPaused || document.hidden) return;
      root.classList.remove('is-paused');
      if (bar) { bar.style.transition = 'transform ' + remaining + 'ms linear'; bar.style.transform = 'scaleX(1)'; }
      schedule();
    }
    root.querySelector('[data-hh-hg-next]').addEventListener('click', function () { show(index + 1); });
    root.querySelector('[data-hh-hg-prev]').addEventListener('click', function () { show(index - 1); });
    if (toggle) toggle.addEventListener('click', function () {
      userPaused = !userPaused;
      toggle.setAttribute('aria-label', userPaused ? toggle.dataset.labelPlay : toggle.dataset.labelPause);
      if (userPaused) hold(); else { remaining = remaining || interval; release(); }
    });
    var copy = root.querySelector('.hh-hg__copy');
    copy.addEventListener('mouseenter', function () { hoverPaused = true; hold(); });
    copy.addEventListener('mouseleave', function () { hoverPaused = false; release(); });
    root.addEventListener('focusin', function () { hoverPaused = true; hold(); });
    root.addEventListener('focusout', function (e) { if (!root.contains(e.relatedTarget)) { hoverPaused = false; release(); } });
    document.addEventListener('visibilitychange', function () { if (document.hidden) hold(); else release(); });
    var x0 = null;
    root.addEventListener('touchstart', function (e) { x0 = e.touches[0].clientX; }, { passive: true });
    root.addEventListener('touchend', function (e) {
      if (x0 === null) return; var dx = e.changedTouches[0].clientX - x0; x0 = null;
      if (Math.abs(dx) > 40) show(index + ((dx < 0) !== (document.documentElement.dir === 'rtl') ? 1 : -1));
    }, { passive: true });
    if (reduced) { root.classList.add('is-paused'); if (toggle) toggle.setAttribute('aria-label', toggle.dataset.labelPlay); }
    function afterLoad() { hydrateScene(1); bar0(); schedule(); }
    if (document.readyState === 'complete') afterLoad(); else window.addEventListener('load', afterLoad, { once: true });
  }

  /* ---------------------------------------------------------------------
     Header contact panel
     --------------------------------------------------------------------- */
  function initContact() {
    var wrap = document.querySelector('[data-hh-ct]');
    if (!wrap) return;
    var btn = wrap.querySelector('[data-hh-ct-btn]');
    var panel = wrap.querySelector('[data-hh-ct-panel]');
    function set(open) {
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      panel.hidden = !open;
      wrap.classList.toggle('is-open', open);
      document.body.classList.toggle('hh-ct-open', open);
      if (open) { var a = panel.querySelector('a'); if (a) a.focus(); }
    }
    btn.addEventListener('click', function (e) { e.stopPropagation(); set(panel.hidden); });
    document.addEventListener('click', function (e) { if (!panel.hidden && !wrap.contains(e.target)) set(false); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && !panel.hidden) { set(false); btn.focus(); } });
  }

  /* ---------------------------------------------------------------------
     Lazy muted videos ([data-hh-dlv-video] video[data-poster]) anywhere
     --------------------------------------------------------------------- */
  function initLazyVideos() {
    var vids = document.querySelectorAll('[data-hh-dlv-video] video[data-poster]:not([data-hh-lv])');
    if (!vids.length) return;
    function arm(v) {
      if (v.dataset.armed) return;
      v.dataset.armed = '1';
      if (v.dataset.poster) v.poster = v.dataset.poster;
      v.querySelectorAll('source[data-src]').forEach(function (s) { s.src = s.dataset.src; });
      v.load();
    }
    if (!('IntersectionObserver' in window)) { vids.forEach(arm); return; }
    var near = new IntersectionObserver(function (es) { es.forEach(function (e) { if (e.isIntersecting) { arm(e.target); near.unobserve(e.target); } }); }, { rootMargin: '600px 0px' });
    var io = new IntersectionObserver(function (es) {
      es.forEach(function (e) {
        if (e.isIntersecting && !reduced) { arm(e.target); var p = e.target.play(); if (p && p.catch) p.catch(function () {}); }
        else e.target.pause();
      });
    }, { threshold: 0.25 });
    vids.forEach(function (v) { v.setAttribute('data-hh-lv', ''); v.muted = true; near.observe(v); io.observe(v); });
  }

  /* --------------------------------------------------------------------- */
  function init() {
    document.querySelectorAll('[data-hh-hero]').forEach(initHero);
    document.querySelectorAll('[data-hh-hg]').forEach(initGalleryHero);
    document.querySelectorAll('[data-hh-room]').forEach(initRoom);
    initSearch();
    initContact();
    initLazyVideos();
    initCollection();
    initLightbox();
    initRecent();
    /* Product cards: the second angle loads on first hover (pointer devices). */
    if (window.matchMedia('(hover: hover)').matches) {
      document.addEventListener('mouseover', function (e) {
        var card = e.target.closest && e.target.closest('.hh-card');
        if (!card || card.dataset.hhSecond) return;
        card.dataset.hhSecond = '1';
        var img = card.querySelector('img.hh-card__img--second[data-srcset]');
        if (img) { img.srcset = img.dataset.srcset; img.removeAttribute('data-srcset'); }
      });
    }
    /* Desktop menu panels: load the panel photograph on first hover/focus. */
    document.querySelectorAll('.hh-hdr__item.has-panel').forEach(function (item) {
      function load() {
        item.querySelectorAll('img[data-srcset]').forEach(function (img) { img.srcset = img.dataset.srcset; img.removeAttribute('data-srcset'); });
      }
      item.addEventListener('mouseenter', load, { once: true });
      item.addEventListener('focusin', load, { once: true });
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  /* Theme editor: re-run when a section is re-rendered. */
  document.addEventListener('shopify:section:load', function (e) {
    e.target.querySelectorAll('[data-hh-hero]').forEach(initHero);
    e.target.querySelectorAll('[data-hh-room]').forEach(initRoom);
  });
})();
